import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
} from '@mui/material';
import { Settings as SettingsIcon, Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { getOrganizationById, updateOrganization } from '@services/organizationService';

const OrgSettingsPage = () => {
  const navigate = useNavigate();
  const { orgId: paramOrgId } = useParams();
  const { userData, isOrgAdmin, refreshUserData } = useAuth();
  const isPlatformAdmin = userData?.userType === 'platform-admin';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState({
    organizationName: '',
    organizationDescription: '',
    website: '',
    contactEmail: '',
    contactPhone: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
  });

  const organizationId = paramOrgId || userData?.organizationId;

  useEffect(() => {
    if (!isOrgAdmin() && !isPlatformAdmin) {
      navigate('/dashboard');
      return;
    }
    if (!organizationId) return;

    const load = async () => {
      try {
        const org = await getOrganizationById(organizationId);
        if (org) {
          setForm({
            organizationName: org.organizationName || '',
            organizationDescription: org.organizationDescription || '',
            website: org.website || '',
            contactEmail: org.contactEmail || '',
            contactPhone: org.contactPhone || '',
            street: org.address?.street || '',
            city: org.address?.city || '',
            state: org.address?.state || '',
            zipCode: org.address?.zipCode || '',
          });
        }
      } catch {
        setError('Failed to load organization settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOrgAdmin, navigate, organizationId]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    if (!form.organizationName.trim()) {
      setError('Organization name is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await updateOrganization(organizationId, {
        organizationName: form.organizationName.trim(),
        organizationDescription: form.organizationDescription.trim(),
        website: form.website.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim(),
        address: {
          street: form.street.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zipCode: form.zipCode.trim(),
        },
      });
      await refreshUserData();
      setSuccessMsg('Settings saved successfully.');
    } catch {
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      {isPlatformAdmin && paramOrgId && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/organizations')} sx={{ mb: 2 }} variant="outlined">
          Back to Organizations
        </Button>
      )}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Organization Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {form.organizationName || userData?.organizationName}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Basic Info */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Basic Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Organization Name"
              value={form.organizationName}
              onChange={handleChange('organizationName')}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={form.organizationDescription}
              onChange={handleChange('organizationDescription')}
              fullWidth
              multiline
              rows={3}
              helperText="A brief description of your organization and its mission."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Website"
              value={form.website}
              onChange={handleChange('website')}
              fullWidth
              placeholder="https://example.org"
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Contact */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Contact Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Email"
              value={form.contactEmail}
              onChange={handleChange('contactEmail')}
              fullWidth
              type="email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Phone"
              value={form.contactPhone}
              onChange={handleChange('contactPhone')}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Address */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Address
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Street Address"
              value={form.street}
              onChange={handleChange('street')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              label="City"
              value={form.city}
              onChange={handleChange('city')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="State"
              value={form.state}
              onChange={handleChange('state')}
              fullWidth
              inputProps={{ maxLength: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="ZIP Code"
              value={form.zipCode}
              onChange={handleChange('zipCode')}
              fullWidth
            />
          </Grid>
        </Grid>

        {/* Save */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        message={successMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default OrgSettingsPage;
